import { DataLayer, calcPrice, priceVatCalc } from "@grandvisionhq/analytics";
import { isDefined } from "@grandvisionhq/utils";
const calcSumPrices = (...args) => calcPrice(args.reduce((sum, n) => sum + (n ?? 0), 0));
const parsePrice = (price, vatRate) => {
  if (!price)
    return {
      regularPrice: null,
      discountedPrice: null,
      discountAmount: null,
      vatAmount: null
    };
  const regularPrice = price.originalPrice;
  const discountedPrice = price.discount?.price ?? null;
  const { vatAmount } = discountedPrice !== null ? priceVatCalc(discountedPrice, vatRate) : priceVatCalc(regularPrice, vatRate);
  return {
    regularPrice,
    discountedPrice,
    discountAmount: discountedPrice ? regularPrice - discountedPrice : 0,
    vatAmount
  };
};
const getPrices = (variant, vat) => {
  if (!variant) {
    console.warn("No variant data found.");
    return null;
  }
  if (typeof vat === "undefined") {
    console.warn(`No VAT amount found for variant with SKU ${variant.sku}.`);
    return null;
  }
  const pricing = typeof variant.pricing.price.originalPrice !== "undefined";
  if (!pricing) {
    console.warn(`Variant with SKU ${variant.sku} doesn't have any pricing.`);
    return null;
  }
  const productPricing = parsePrice(variant.pricing.price, vat);
  return {
    original: calcSumPrices(productPricing.regularPrice),
    discountedPrice: productPricing.discountedPrice !== null ? calcSumPrices(productPricing.discountedPrice) : calcSumPrices(productPricing.regularPrice),
    discountAmount: calcSumPrices(productPricing.discountAmount),
    vatAmount: calcSumPrices(productPricing.vatAmount)
  };
};
const getSharedAttributes = (product, purchaseType, activeVariant, lineItemType) => {
  const {
    DataLayerTypes: { Stock, BrandType }
  } = DataLayer;
  const { sku } = activeVariant;
  const prices = getPrices(activeVariant, product.includedVatAmount);
  const name = [
    ...new Set(
      [activeVariant.brand.name, activeVariant.brand.model, activeVariant.brand.modelCode].filter(
        Boolean
      )
    )
  ].join(" ").trim();
  let dimension193 = "";
  if (isDefined(activeVariant.isExclusiveBrand)) {
    dimension193 = activeVariant.isExclusiveBrand ? BrandType.OwnBrand : BrandType.ExternalBrand;
  }
  return {
    name,
    id: sku || "",
    price: prices?.discountedPrice || "",
    dimension143: purchaseType,
    dimension144: lineItemType,
    dimension145: "true",
    dimension190: `${activeVariant.erpId}`,
    dimension193,
    dimension194: product.isOnStock ? Stock.InStock : Stock.OutOfStock,
    metric140: prices?.original || "",
    metric141: prices?.discountAmount || "",
    metric142: prices?.vatAmount || ""
  };
};
const getSunglassesAttributes = (sharedAttributes, product, activeVariant) => ({
  ...sharedAttributes,
  category: DataLayer.DataLayerTypes.Category.Sunglasses,
  brand: activeVariant.brand.name || "",
  variant: activeVariant.frame.shape.en?.toLowerCase() || "",
  dimension16: activeVariant.gender.en?.toLowerCase() || "",
  dimension140: product.masterVariant.sku || "",
  dimension146: activeVariant.frame.material.en?.toLowerCase() || "",
  dimension147: activeVariant.frame.color.en?.toLowerCase() || "",
  dimension191: activeVariant.frame.type.en?.toLowerCase() || "",
  dimension192: activeVariant.faceShape?.en.toLowerCase() || ""
});
const getContactLensesAttributes = (sharedAttributes, product, activeVariant) => ({
  ...sharedAttributes,
  name: activeVariant.name || "",
  brand: activeVariant.brand.name,
  category: DataLayer.DataLayerTypes.Category.ContactLenses,
  variant: activeVariant.lensType?.en || "",
  dimension140: product.masterVariant.sku || "",
  dimension148: activeVariant.replacementFrequency || "",
  dimension146: activeVariant.material
});
const getPrescriptionGlassesAttributes = (sharedAttributes, product, activeVariant) => {
  const frameColor = activeVariant.frame.color.en?.toLowerCase() || "";
  const frameMaterial = activeVariant.frame.material.en?.toLowerCase() || "";
  return {
    ...sharedAttributes,
    brand: activeVariant.brand.name,
    variant: activeVariant.frame.shape.en?.toLowerCase() || "",
    dimension140: product.masterVariant.sku,
    dimension16: activeVariant.gender.en?.toLocaleLowerCase() || "",
    dimension191: activeVariant.frame.type.en?.toLowerCase() || "",
    dimension192: activeVariant.faceShape?.en.toLowerCase() || "",
    category: DataLayer.DataLayerTypes.Category.Optical,
    ...frameMaterial ? { dimension146: frameMaterial } : null,
    ...frameColor ? { dimension147: frameColor } : null
  };
};
const getSolutionsAttributes = (sharedAttributes, product, activeVariant) => ({
  ...sharedAttributes,
  name: activeVariant.name,
  category: DataLayer.DataLayerTypes.Category.Solutions,
  brand: activeVariant.brand.name,
  variant: activeVariant.packageOption || "",
  dimension140: String(product.masterVariant.sku),
  dimension146: activeVariant.solutionType
});
const getReadyReadersAttributes = (sharedAttributes, product, activeVariant) => ({
  ...sharedAttributes,
  category: DataLayer.DataLayerTypes.Category.ReadyReaders,
  brand: activeVariant.brand.name,
  variant: activeVariant.frame.shape.en?.toLowerCase() || "",
  dimension16: activeVariant.gender.en?.toLocaleLowerCase() || "",
  dimension140: product.masterVariant.sku,
  dimension146: activeVariant.frame.material.en?.toLowerCase() || "",
  dimension147: activeVariant.frame.color.en?.toLowerCase() || "",
  dimension191: activeVariant.frame.type.en?.toLowerCase() || "",
  dimension192: activeVariant.faceShape?.en.toLowerCase() || ""
});
const getAccessoriesAttributes = (sharedAttributes, product, activeVariant) => ({
  ...sharedAttributes,
  category: DataLayer.DataLayerTypes.Category.Accessories,
  brand: activeVariant.brand.name,
  variant: activeVariant.accessoryType.en?.toLowerCase() || "",
  dimension16: activeVariant.gender.en?.toLocaleLowerCase() || "",
  dimension146: activeVariant.material?.toLowerCase() || "",
  dimension147: activeVariant.accessoryColor.en?.toLowerCase() || "",
  dimension140: product.masterVariant.sku || ""
});
const mapProductListingToDataLayerProductObject = (purchaseType, product, activeSku) => {
  const activeVariant = [product.masterVariant, ...product.variants].find((variant) => variant.sku === activeSku) || product.masterVariant;
  const sharedAttributes = getSharedAttributes(
    product,
    purchaseType,
    activeVariant,
    DataLayer.DataLayerTypes.LineItemType.Single
  );
  if (product.productType === "Sunglasses") {
    return getSunglassesAttributes(sharedAttributes, product, activeVariant);
  }
  if (product.productType === "Contact-lenses") {
    return getContactLensesAttributes(sharedAttributes, product, activeVariant);
  }
  if (product.productType === "Frames") {
    return getPrescriptionGlassesAttributes(sharedAttributes, product, activeVariant);
  }
  if (product.productType === "Ready-readers") {
    return getReadyReadersAttributes(sharedAttributes, product, activeVariant);
  }
  if (product.productType === "Accessories") {
    return getAccessoriesAttributes(sharedAttributes, product, activeVariant);
  }
  if (product.productType === "Solutions") {
    return getSolutionsAttributes(sharedAttributes, product, activeVariant);
  }
};
export {
  mapProductListingToDataLayerProductObject
};
//# sourceMappingURL=ProductListingMapper.js.map
